import type { ReactNode } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AlertCircle, Check } from 'lucide-react';
import type { CostSummary } from '../../lib/api';
import {
  COST_TEXT_KEYS,
  formatMoney,
  monthName as englishMonthName,
} from '../../content/site';
import { useSiteConfig } from '../../lib/siteConfig';
import { useLanguage, useT } from '../../i18n';
import { ChipInButton } from './SiteChrome';

// What Twinden costs and what families have paid, as published from the
// admin panel (M43, docs/costs-and-contributions.md). Amounts stay ₹ with
// Indian grouping in every language; months follow the page's (D146).

/** Month names in the page's language: "November 2026" / "नवंबर 2026". */
export function useMonthName(): (month: string) => string {
  const locale = useLanguage() === 'hi' ? 'hi-IN' : 'en-IN';
  return (month) => englishMonthName(month, locale);
}

/**
 * Cost items are typed in the admin panel, in English. The default ones,
 * still word for word, are shown in the page's language; others as typed.
 */
function useCostText(): (text: string) => string {
  const t = useT();
  return (text) => {
    const key = COST_TEXT_KEYS.get(text);
    return key ? t(key) : text;
  };
}

function Row({
  label,
  detail,
  amount,
  muted,
}: {
  label: ReactNode;
  detail?: string | null;
  amount: ReactNode;
  muted?: boolean;
}) {
  return (
    <HStack
      align="flex-start"
      spacing={4}
      py={3}
      borderTopWidth="1px"
      borderColor="line"
      _first={{ borderTopWidth: 0, pt: 0 }}
      color={muted ? 'muted' : undefined}
    >
      <Box flex="1" minW={0}>
        <Text fontWeight={700}>{label}</Text>
        {detail && (
          <Text color="muted" fontSize="15px">
            {detail}
          </Text>
        )}
      </Box>
      <Text fontWeight={700} whiteSpace="nowrap">
        {amount}
      </Text>
    </HStack>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | null;
}) {
  return (
    <Box bg="surface.subtle" borderRadius="16px" px={4} py={3}>
      <Text fontSize="13px" color="muted" fontWeight={600}>
        {label}
      </Text>
      <Text fontSize="20px" fontWeight={800}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="13px" color="muted">
          {hint}
        </Text>
      )}
    </Box>
  );
}

/** "from 11 families" under what was paid (D196), or nothing uncounted. */
export function useFamiliesCountText(): (
  count: number | null
) => string | null {
  const t = useT();
  return (count) =>
    !count
      ? null
      : t(
          count === 1
            ? 'site.costs.fromFamiliesOne'
            : 'site.costs.fromFamiliesOther',
          { count }
        );
}

function Standing({ costs }: { costs: CostSummary }) {
  const t = useT();
  const monthName = useMonthName();
  const available = costs.carriedIn + costs.received;
  const covered =
    costs.total > 0
      ? Math.min(100, Math.round((available / costs.total) * 100))
      : 100;
  const ahead =
    costs.coveredThrough && costs.coveredThrough !== costs.month
      ? t('site.costs.coveredThrough', {
          month: monthName(costs.coveredThrough),
        })
      : t('site.costs.covered');
  return (
    <VStack align="stretch" spacing={2}>
      <Box
        h="10px"
        borderRadius="full"
        bg="surface.subtle"
        overflow="hidden"
        role="progressbar"
        aria-label={t('site.costs.progressLabel')}
        aria-valuenow={covered}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <Box h="full" w={`${Math.max(covered, 2)}%`} bg="accent.solid" />
      </Box>
      <HStack spacing={2} color={costs.balance >= 0 ? 'accent' : 'danger'}>
        {costs.balance >= 0 ? <Check size={18} /> : <AlertCircle size={18} />}
        <Text fontWeight={700}>
          {costs.balance >= 0
            ? ahead
            : t('site.costs.short', {
                amount: formatMoney(-costs.balance),
                percent: covered,
              })}
        </Text>
      </HStack>
    </VStack>
  );
}

export function CostsCard() {
  const config = useSiteConfig();
  const costs = config?.costs;
  const t = useT();
  const monthName = useMonthName();
  const costText = useCostText();
  const familiesText = useFamiliesCountText();
  // The mark while the month's figures load (M66, D248).
  const costsState = useLoadingState({ loading: config === undefined });

  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={{ base: 4, md: 6 }}
    >
      {config === undefined || costsState.show ? (
        <LoadingPanel state={costsState} />
      ) : !costs ? (
        <Text color="muted">{t('site.costs.soon')}</Text>
      ) : (
        <VStack align="stretch" spacing={5}>
          <HStack justify="space-between" align="baseline">
            <Text fontSize="20px" fontWeight={800}>
              {monthName(costs.month)}
            </Text>
            {costs.estimated && (
              <Badge
                colorScheme="gray"
                borderRadius="full"
                px={2}
                textTransform="none"
              >
                {t('site.costs.estimated')}
              </Badge>
            )}
          </HStack>

          <VStack align="stretch" spacing={0}>
            {costs.items
              .filter((item) => !item.upcoming)
              .map((item) => (
                <Row
                  key={item.label}
                  label={costText(item.label)}
                  detail={item.detail && costText(item.detail)}
                  amount={formatMoney(item.amount)}
                />
              ))}
            <HStack
              spacing={4}
              pt={4}
              mt={1}
              borderTopWidth="2px"
              borderColor="ink"
            >
              <Text flex="1" fontWeight={800} fontSize="18px">
                {t('site.costs.total')}
              </Text>
              <Text fontWeight={800} fontSize="18px" whiteSpace="nowrap">
                {formatMoney(costs.total)}
              </Text>
            </HStack>
            {costs.items
              .filter((item) => item.upcoming)
              .map((item) => (
                <Box key={item.label} pt={3}>
                  <Row
                    label={t('site.costs.coming', {
                      label: costText(item.label),
                    })}
                    detail={item.detail && costText(item.detail)}
                    amount={`+${formatMoney(item.amount)}`}
                    muted
                  />
                </Box>
              ))}
          </VStack>

          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
            <Stat
              label={t('site.costs.paidByFamilies')}
              value={formatMoney(costs.received)}
              hint={familiesText(costs.familiesCount)}
            />
            <Stat
              label={t('site.costs.carriedIn')}
              value={formatMoney(costs.carriedIn)}
            />
            <Stat
              label={t('site.costs.balance')}
              value={formatMoney(costs.balance)}
            />
          </SimpleGrid>

          <Standing costs={costs} />

          {costs.note && (
            <Text color="ink.body" fontSize="15px">
              {costs.note}
            </Text>
          )}

          {costs.history.length > 1 && (
            <Box>
              <Text fontWeight={700} pb={2}>
                {t('site.costs.recent')}
              </Text>
              <VStack align="stretch" spacing={1}>
                <HStack color="muted" fontSize="13px" fontWeight={600}>
                  <Text flex="1">{t('site.costs.month')}</Text>
                  <Text w="96px" textAlign="right">
                    {t('site.costs.costs')}
                  </Text>
                  <Text w="96px" textAlign="right">
                    {t('site.costs.paid')}
                  </Text>
                </HStack>
                {costs.history.map((row) => (
                  <HStack key={row.month} fontSize="15px">
                    <Text flex="1">{monthName(row.month)}</Text>
                    <Text w="96px" textAlign="right">
                      {formatMoney(row.total)}
                    </Text>
                    <Text w="96px" textAlign="right">
                      {formatMoney(row.received)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <Text color="muted" fontSize="14px">
            {t('site.costs.leftOver')}
            {costs.estimated && t('site.costs.estimatesNote')}
          </Text>

          {config?.supportUrl && <ChipInButton variant="solid" />}
        </VStack>
      )}
    </Box>
  );
}
